/**
 * Cart Service
 * Handles cart operations for authenticated users via backend API
 */

import { api } from '../../lib/api';
import {
  CartItem,
  CartItemInput,
  CartResponse,
  TransferResult,
  GroupedCart,
} from './cartTypes';
import { getAnonymousCart, clearAnonymousCart, setAnonymousCart } from './anonymousCartService';

/** Minimal listing fields needed to render a guest cart item. */
interface ListingSummary {
  _id: string;
  name: string;
  title: string;
  paymentMethod: 'paypal' | 'stripe';
  timezone: string;
}

/**
 * Resolve anonymous cart items from sessionStorage into CartItem shapes
 * by fetching each unique listing via the public GET /listings/:id endpoint.
 * Items whose listing lookup fails are dropped so one bad listing cannot
 * blank the whole cart.
 */
export const fetchAnonymousCartDetails = async (): Promise<CartItem[]> => {
  const anonymousItems = getAnonymousCart();
  if (anonymousItems.length === 0) {
    return [];
  }

  const uniqueListingIds = [...new Set(anonymousItems.map((item) => item.listingId))];
  const listingCache = new Map<string, ListingSummary>();

  await Promise.all(
    uniqueListingIds.map(async (listingId) => {
      try {
        const data = await api.get<{ listing: ListingSummary }>(`/listings/${listingId}`);
        if (data?.listing) {
          listingCache.set(listingId, data.listing);
        }
      } catch (error) {
        console.error(`Failed to fetch listing ${listingId} for anonymous cart:`, error);
      }
    })
  );

  return anonymousItems
    .filter((item) => listingCache.has(item.listingId))
    .map((item) => {
      const listing = listingCache.get(item.listingId)!;
      return {
        listingId: {
          _id: listing._id || item.listingId,
          name: listing.name,
          title: listing.title,
          paymentMethod: listing.paymentMethod,
          timezone: listing.timezone,
        },
        windowId: { _id: item.windowId },
        startDateTime: item.startDateTime,
        endDateTime: item.endDateTime,
        price: item.price,
        renterDetails: item.renterDetails,
      };
    });
};

/**
 * Fetch user's cart from backend
 */
export const fetchCart = async (): Promise<CartResponse> => {
  try {
    const data = await api.get<CartResponse>('/cart');
    return data;
  } catch (error) {
    // Don't log auth errors as they're handled by the API client
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'status' in error.response &&
      error.response.status !== 401
    ) {
      console.error('Failed to fetch cart:', error);
    }
    throw error;
  }
};

/**
 * Add item to authenticated user's cart
 */
export const addItemToCart = async (item: CartItemInput): Promise<CartResponse> => {
  try {
    const data = await api.post<CartResponse>('/cart/items', item);
    return data;
  } catch (error) {
    console.error('Failed to add item to cart:', error);
    throw error;
  }
};

/**
 * Remove item from authenticated user's cart
 */
export const removeItemFromCart = async (index: number): Promise<CartResponse> => {
  try {
    const data = await api.delete<CartResponse>(`/cart/items/${index}`);
    return data;
  } catch (error) {
    console.error('Failed to remove item from cart:', error);
    throw error;
  }
};

/**
 * Clear authenticated user's cart
 */
export const clearCart = async (): Promise<void> => {
  try {
    await api.delete('/cart');
  } catch (error) {
    console.error('Failed to clear cart:', error);
    throw error;
  }
};

/**
 * Transfer anonymous cart items to authenticated user's cart
 * Called when user logs in
 */
export const transferAnonymousCart = async (): Promise<TransferResult> => {
  const anonymousItems = getAnonymousCart();

  if (anonymousItems.length === 0) {
    return { success: true, transferred: 0, failed: 0 };
  }

  let transferred = 0;
  const remaining: CartItemInput[] = [];

  // Transfer each item to the backend
  for (const item of anonymousItems) {
    try {
      await api.post<CartResponse>('/cart/items', item);
      transferred++;
    } catch (error) {
      console.error('Failed to transfer cart item:', error);
      remaining.push(item);
    }
  }

  // Keep whatever failed rather than dropping it: a slot taken while the
  // visitor was signing in should stay visible and retryable, not vanish.
  if (remaining.length > 0) {
    setAnonymousCart(remaining);
  } else {
    clearAnonymousCart();
  }

  return { success: remaining.length === 0, transferred, failed: remaining.length };
};

/**
 * Fetch user's cart grouped by owner and payment method
 */
export const fetchGroupedCart = async (): Promise<GroupedCart> => {
  try {
    const data = await api.get<GroupedCart>('/cart/grouped');
    return data;
  } catch (error) {
    console.error('Failed to fetch grouped cart:', error);
    throw error;
  }
};

